import { ReactElement, useEffect } from 'react';

import { Flex, MEDIA_QUERY, OverlayPortal, useResponsive, ZINDEX } from '@bucketplace/design-system';
import { IconXCircleFilled } from '@bucketplace/icons';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

interface VideoPreviewProps {
  type: 'iframe' | 'video';
  onClose: () => void;
  url: string;
  title: string;
  autoPlay?: boolean;
}

export function VideoPreview({
  type,
  url,
  title,
  autoPlay=true,
  onClose,
}: VideoPreviewProps): ReactElement {
  const { breakpoint } = useResponsive({});
  const isMobile = breakpoint === 'mobile' || breakpoint === 'tablet';

  const Priview = type === 'iframe'
    ? (
      <iframe
        title={title}
        src={url}
        frameBorder="0"
        width="100%"
        height="100%"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
        sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox allow-presentation allow-top-navigation-by-user-activation"
      />
    ): (
      <Video
        src={url}
        autoPlay={autoPlay}
        controls
        controlsList="nodownload noplaybackrate noremoteplayback nofullscreen"
        playsInline
        disablePictureInPicture
        preload="metadata"
      >
        <track kind="captions" src="" srcLang="ko" label="Korean" default />
      </Video>
    );

  useEffect(() => {
    document.getElementsByTagName('body')[0].style.overflow = 'hidden';
    return () => {
      document.getElementsByTagName('body')[0].style.overflow = 'auto';
    };
  }, []);

  return (
    <OverlayPortal>
      <Flex
        position="fixed"
        top="0"
        left="0"
        w="100vw"
        h="100vh"
        align="center"
        justify="center"
        zIndex={ZINDEX.modal}
        css={{
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
        }}
      >
        <Flex
          w="100vw"
          h={type === 'iframe' ? '80vh' : '100%'}
          align="center"
          justify="center"
        >
          { Priview }
        </Flex>
        <IconXCircleFilled
          size={isMobile ? 40 : 60}
          aspect="width"
          weight="extralight"
          renderMode="multicolor"
          color="rgba(255, 255, 255, 0.20)"
          style={{
            position: 'absolute',
            top: '24px',
            right: '24px',
            cursor: 'pointer',
            zIndex: 1,
          }}
          onClick={onClose}
        />
      </Flex>
    </OverlayPortal>
  );
}

const Video = styled.video`
  aspect-ratio: 300 / 530;
  object-fit: cover;
  width: 100%;
  height: auto;
  min-height: 0px;
  padding: 36px;
  box-sizing: border-box;

  ${MEDIA_QUERY.min('tablet', css`
    width: auto;
    height: 100%;
    max-height: 66.4vh;
    padding: 0;
  `)}
`;
